import React, { useState, useEffect, useRef } from 'react';
import { api } from '@/App';
import usePeriodChange from '@/hooks/usePeriodChange';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Receipt, Trash2, ArrowRightLeft } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/SearchableSelect';
import { toast } from 'sonner';

const Expenses = ({ user, branchId, onDataChange }) => {
  const [expenses, setExpenses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchAccounts, setBranchAccounts] = useState([]);
  const [branchName, setBranchName] = useState('');
  const [activePeriod, setActivePeriod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availablePartais, setAvailablePartais] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedExpenses, setSelectedExpenses] = useState([]);
  const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
  const [bulkPartai, setBulkPartai] = useState('');
  // New states for move feature
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [activePeriods, setActivePeriods] = useState([]);
  const [isClosedPeriod, setIsClosedPeriod] = useState(false);
  const [selectedTargetPeriod, setSelectedTargetPeriod] = useState('');
  // Bulk input state
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkFormData, setBulkFormData] = useState({
    branch_account_id: '',
    partai: '',
    items: [
      {expense_date: new Date().toISOString().split('T')[0], amount: '', description: ''},
      {expense_date: new Date().toISOString().split('T')[0], amount: '', description: ''},
      {expense_date: new Date().toISOString().split('T')[0], amount: '', description: ''},
      {expense_date: new Date().toISOString().split('T')[0], amount: '', description: ''},
      {expense_date: new Date().toISOString().split('T')[0], amount: '', description: ''}
    ]
  });
  // Refs for keyboard navigation in bulk form
  const inputRefs = useRef([]);
  
  const [formData, setFormData] = useState({
    branch_id: branchId || '',
    branch_account_id: '',
    partai: '',
    amount: '',
    description: '',
    expense_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchData();
    fetchActivePeriods();
  }, [branchId]);

  usePeriodChange(() => fetchData(), { includeSettings: true });

  useEffect(() => {
    if (branchId) {
      setFormData(prev => ({ ...prev, branch_id: branchId }));
      loadBranchAccounts(branchId);
    }
  }, [branchId]);

  const loadBranchAccounts = async (brId) => {
    try {
      const branchRes = await api.get(`/branches/${brId}`);
      setBranchAccounts(branchRes.data.accounts || []);
      setBranchName(branchRes.data.name || '');
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    }
  };

  const fetchData = async () => {
    try {
      // Get selected period from localStorage
      const periodId = localStorage.getItem('selectedPeriodId');
      
      // Build query params
      const params = periodId ? { period_id: periodId } : {};
      
      // OPTIMIZED: All API calls in parallel for better performance
      const [expensesRes, branchesRes, purchasesRes, activePeriodRes] = await Promise.all([
        api.get('/expenses', { params }),
        api.get('/branches'),
        api.get('/purchases', { params }),
        api.get('/periods/active').catch(() => null) // Catch error if no active period
      ]);
      
      // Filter expenses by branchId if provided
      const filteredExpenses = branchId 
        ? expensesRes.data.filter(e => e.branch_id === branchId)
        : expensesRes.data;
      
      setExpenses(filteredExpenses);
      setBranches(branchesRes.data);
      
      // Get unique partais from purchases
      const filteredPurchases = branchId
        ? purchasesRes.data.filter(p => p.branch_id === branchId)
        : purchasesRes.data;
      const partais = [...new Set(filteredPurchases.map(p => p.partai))].filter(Boolean);
      setAvailablePartais(partais);
      
      if (activePeriodRes) {
        setActivePeriod(activePeriodRes.data);
      }
      
      // Notify parent component to refresh its data (for card totals)
      if (onDataChange) {
        onDataChange();
      }
    } catch (error) {
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  const handleBranchChange = async (branchId) => {
    // Handle "none" value as empty/null
    const actualBranchId = branchId === 'none' ? '' : branchId;
    setFormData({ ...formData, branch_id: actualBranchId, branch_account_id: '' });
    
    if (actualBranchId) {
      try {
        const branchRes = await api.get(`/branches/${actualBranchId}`);
        setBranchAccounts(branchRes.data.accounts || []);
      } catch (error) {
        console.error('Failed to fetch accounts:', error);
      }
    } else {
      setBranchAccounts([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!activePeriod) {
      toast.error('Tidak ada periode aktif. Silakan buat periode terlebih dahulu.');
      return;
    }

    if (!formData.branch_account_id) {
      toast.error('Pilih rekening cabang terlebih dahulu');
      return;
    }

    // Validate partai exists in purchases
    if (formData.partai && !availablePartais.includes(formData.partai)) {
      toast.error(`Partai "${formData.partai}" tidak ditemukan di data pembelian`);
      return;
    }

    setLoading(true);

    try {
      // Get selected period from localStorage
      const selectedPeriodId = localStorage.getItem('selectedPeriodId');
      
      const payload = {
        branch_id: formData.branch_id,
        branch_account_id: formData.branch_account_id,
        partai: formData.partai,
        amount: parseFloat(formData.amount),
        description: formData.description,
        expense_date: new Date(formData.expense_date).toISOString(),
        period_id: selectedPeriodId || null  // Send selected period to backend
      };

      if (editingId) {
        await api.put(`/expenses/${editingId}`, payload);
        toast.success('Biaya berhasil diperbarui');
      } else {
        await api.post('/expenses', payload);
        toast.success('Biaya berhasil ditambahkan');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operasi gagal');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (expense) => {
    setEditingId(expense.id);
    setFormData({
      branch_id: expense.branch_id,
      branch_account_id: expense.branch_account_id,
      partai: expense.partai,
      amount: expense.amount.toString(),
      description: expense.description,
      expense_date: new Date(expense.expense_date).toISOString().split('T')[0]
    });
    // Load branch accounts for the expense's branch
    loadBranchAccounts(expense.branch_id);
    setDialogOpen(true);
  };

  const handleDeleteClick = (id) => {
    setDeletingId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingId) return;

    setLoading(true);
    try {
      await api.delete(`/expenses/${deletingId}`);
      toast.success('Biaya berhasil dihapus');
      setDeleteDialogOpen(false);
      setDialogOpen(false);
      setDeletingId(null);
      resetForm();
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus biaya');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelectExpense = (expenseId) => {
    setSelectedExpenses(prev => 
      prev.includes(expenseId) 
        ? prev.filter(id => id !== expenseId)
        : [...prev, expenseId]
    );
  };

  const togglePartaiExpenses = (ids) => {
    const allSelected = ids.every(id => selectedExpenses.includes(id));
    setSelectedExpenses(prev => allSelected
      ? prev.filter(id => !ids.includes(id))
      : [...new Set([...prev, ...ids])]
    );
  };

  const toggleSelectAll = () => {
    if (selectedExpenses.length === expenses.length) {
      setSelectedExpenses([]);
    } else {
      setSelectedExpenses(expenses.map(e => e.id));
    }
  };

  const fetchActivePeriods = async () => {
    try {
      const response = await api.get('/periods');
      const currentPeriodId = localStorage.getItem('selectedPeriodId');
      
      // Get current period to compare dates
      const currentPeriod = response.data.find(p => p.id === currentPeriodId);
      setIsClosedPeriod(!!currentPeriod?.is_closed);
      
      if (!currentPeriod) {
        setActivePeriods([]);
        return;
      }
      
      const currentStartDate = new Date(currentPeriod.start_date);
      
      // Filter: only non-closed periods with start_date AFTER current period (future periods)
      const futurePeriods = response.data.filter(p => {
        if (p.is_closed || p.id === currentPeriodId) return false;
        const periodStartDate = new Date(p.start_date);
        return periodStartDate > currentStartDate; // Only future periods
      });
      
      setActivePeriods(futurePeriods);
    } catch (error) {
      console.error('Failed to fetch active periods:', error);
    }
  };

  const handleMoveToPeriod = async () => {
    if (!selectedTargetPeriod) {
      toast.error('Pilih periode tujuan terlebih dahulu');
      return;
    }

    // Validate all selected expenses are from the same branch
    const selectedExpenseItems = expenses.filter(e => selectedExpenses.includes(e.id));
    const branches = [...new Set(selectedExpenseItems.map(e => e.branch_id))];
    if (branches.length > 1) {
      toast.error('Hanya bisa memindahkan biaya dari cabang yang sama');
      return;
    }

    setLoading(true);
    try {
      await api.post('/expenses/move-to-period', {
        expense_ids: selectedExpenses,
        target_period_id: selectedTargetPeriod
      });
      
      toast.success(`${selectedExpenses.length} biaya berhasil dipindahkan ke periode lain`);
      setMoveDialogOpen(false);
      setSelectedExpenses([]);
      setSelectedTargetPeriod('');
      
      // Refresh data - don't let fetchData errors bubble up
      try {
        await fetchData();
      } catch (fetchError) {
        console.error('Failed to refresh data after move:', fetchError);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memindahkan biaya');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkEditPartai = async () => {
    if (!bulkPartai.trim()) {
      toast.error('Partai tidak boleh kosong');
      return;
    }

    setLoading(true);
    try {
      // Update each selected expense
      await Promise.all(
        selectedExpenses.map(expenseId => {
          const expense = expenses.find(e => e.id === expenseId);
          return api.put(`/expenses/${expenseId}`, {
            ...expense,
            partai: bulkPartai
          });
        })
      );
      
      toast.success(`${selectedExpenses.length} biaya berhasil diupdate`);
      setBulkEditDialogOpen(false);
      setBulkPartai('');
      setSelectedExpenses([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengupdate partai');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate
      if (!bulkFormData.branch_account_id) {
        toast.error('Pilih rekening cabang terlebih dahulu');
        setLoading(false);
        return;
      }
      
      // Filter out empty items (skip items with empty amount or description)
      const validItems = bulkFormData.items.filter(item => 
        item.expense_date && item.amount && item.amount.trim() !== '' && item.description && item.description.trim() !== ''
      );
      
      if (validItems.length === 0) {
        toast.error('Mohon isi minimal 1 biaya dengan lengkap');
        setLoading(false);
        return;
      }

      const submitData = {
        branch_id: branchId,
        branch_account_id: bulkFormData.branch_account_id,
        partai: bulkFormData.partai || '',
        period_id: localStorage.getItem('selectedPeriodId') || null,
        items: validItems.map(item => ({
          expense_date: new Date(item.expense_date).toISOString(),
          amount: parseFloat(item.amount),
          description: item.description
        }))
      };

      await api.post('/expenses/bulk', submitData);
      toast.success(`${validItems.length} biaya berhasil ditambahkan`);
      
      setBulkDialogOpen(false);
      resetBulkForm();
      fetchData();
      if (onDataChange) onDataChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operasi gagal');
    } finally {
      setLoading(false);
    }
  };
  
  const addBulkItem = () => {
    setBulkFormData({
      ...bulkFormData,
      items: [...bulkFormData.items, {expense_date: new Date().toISOString().split('T')[0], amount: '', description: ''}]
    });
  };
  
  const removeBulkItem = (index) => {
    if (bulkFormData.items.length <= 1) {
      toast.error('Harus ada minimal 1 item');
      return;
    }
    setBulkFormData({
      ...bulkFormData,
      items: bulkFormData.items.filter((_, i) => i !== index)
    });
  };
  
  const updateBulkItem = (index, field, value) => {
    const newItems = [...bulkFormData.items];
    
    // If updating amount, handle thousand separator
    if (field === 'amount') {
      // Remove all non-numeric characters
      const numericValue = value.replace(/[^\d]/g, '');
      newItems[index][field] = numericValue;
    } else {
      newItems[index][field] = value;
    }
    
    setBulkFormData({
      ...bulkFormData,
      items: newItems
    });
  };

  // Format number with thousand separator for display
  const formatBulkAmount = (amount) => {
    if (!amount) return '';
    return new Intl.NumberFormat('id-ID').format(amount);
  };

  const resetBulkForm = () => {
    setBulkFormData({
      branch_account_id: '',
      partai: '',
      items: [
        {expense_date: new Date().toISOString().split('T')[0], amount: '', description: ''},
        {expense_date: new Date().toISOString().split('T')[0], amount: '', description: ''},
        {expense_date: new Date().toISOString().split('T')[0], amount: '', description: ''},
        {expense_date: new Date().toISOString().split('T')[0], amount: '', description: ''},
        {expense_date: new Date().toISOString().split('T')[0], amount: '', description: ''}
      ]
    });
  };

  // Keyboard navigation handler for bulk form
  const handleKeyDown = (e, rowIndex, field) => {
    const totalRows = bulkFormData.items.length;
    const fields = ['expense_date', 'amount', 'description'];
    const currentFieldIndex = fields.indexOf(field);
    
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // If on last row and last field, add new row
      if (rowIndex === totalRows - 1 && field === 'description') {
        addBulkItem();
        // Focus will be set after state update
        setTimeout(() => {
          const newRowIndex = totalRows;
          const refKey = `${newRowIndex}-expense_date`;
          if (inputRefs.current[refKey]) {
            inputRefs.current[refKey].focus();
          }
        }, 100);
      } else {
        // Move to next row, same field
        const nextRowIndex = rowIndex + 1;
        if (nextRowIndex < totalRows) {
          const refKey = `${nextRowIndex}-${field}`;
          if (inputRefs.current[refKey]) {
            inputRefs.current[refKey].focus();
          }
        }
      }
    } else if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      
      // Move to next field in same row
      const nextFieldIndex = currentFieldIndex + 1;
      if (nextFieldIndex < fields.length) {
        const refKey = `${rowIndex}-${fields[nextFieldIndex]}`;
        if (inputRefs.current[refKey]) {
          inputRefs.current[refKey].focus();
        }
      } else if (rowIndex + 1 < totalRows) {
        // Move to first field of next row
        const refKey = `${rowIndex + 1}-expense_date`;
        if (inputRefs.current[refKey]) {
          inputRefs.current[refKey].focus();
        }
      }
    } else if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      
      // Move to previous field
      const prevFieldIndex = currentFieldIndex - 1;
      if (prevFieldIndex >= 0) {
        const refKey = `${rowIndex}-${fields[prevFieldIndex]}`;
        if (inputRefs.current[refKey]) {
          inputRefs.current[refKey].focus();
        }
      } else if (rowIndex > 0) {
        // Move to last field of previous row
        const refKey = `${rowIndex - 1}-description`;
        if (inputRefs.current[refKey]) {
          inputRefs.current[refKey].focus();
        }
      }
    }
  };

  const resetForm = () => {
    setFormData({
      branch_id: branchId || '',
      branch_account_id: '',
      partai: '',
      amount: '',
      description: '',
      expense_date: new Date().toISOString().split('T')[0]
    });
    setEditingId(null);
  };

  const getBranchName = (branchId) => {
    return branches.find(b => b.id === branchId)?.name || '-';
  };

  const getAccountName = (branchId, accountId) => {
    const branch = branches.find(b => b.id === branchId);
    if (!branch || !branch.accounts) return '-';
    const account = branch.accounts.find(a => a.id === accountId);
    return account ? account.account_name : '-';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount).replace('Rp', 'Rp.');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Helper for thousand separator in input
  const formatNumber = (value) => {
    if (!value) return '';
    // Remove non-numeric characters except decimal point
    const numericValue = value.toString().replace(/[^\d.]/g, '');
    // Format with thousand separator
    return new Intl.NumberFormat('id-ID').format(numericValue);
  };

  const parseFormattedNumber = (value) => {
    if (!value) return '';
    // Remove thousand separators (dots in Indonesian format)
    return value.toString().replace(/\./g, '');
  };

  const handleAmountChange = (e) => {
    const rawValue = e.target.value;
    // Remove all non-numeric characters except decimal point
    const numericValue = rawValue.replace(/[^\d]/g, '');
    // Store the raw numeric value
    setFormData({ ...formData, amount: numericValue });
  };

  if (loading && expenses.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  const lockedByClosedPeriod = isClosedPeriod && !!editingId;

  return (
    <div className="space-y-6" data-testid="expenses-section">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700" data-testid="add-expense-btn">
                <Plus size={20} className="mr-2" />
                Tambah Biaya
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingId ? 'Edit Biaya' : 'Tambah Biaya Baru'}</DialogTitle>
              </DialogHeader>
            {lockedByClosedPeriod && (
              <div
                className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800"
                data-testid="closed-period-expense-edit-notice"
              >
                Periode sudah ditutup. Hanya <strong>Partai</strong> dan <strong>Deskripsi</strong> yang dapat diubah.
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="account">Pilih Rekening Cabang *</Label>
                <SearchableSelect
                  value={formData.branch_account_id}
                  onValueChange={(value) => setFormData({ ...formData, branch_account_id: value })}
                  options={branchAccounts.map(account => ({
                    value: account.id,
                    label: `${account.account_name} (Saldo: Rp.\u00a0${account.balance.toLocaleString('id-ID')})`
                  }))}
                  placeholder="Pilih rekening..."
                  searchPlaceholder="Cari rekening..."
                  required
                  disabled={lockedByClosedPeriod}
                />
              </div>

              <div>
                <Label htmlFor="expense_date">Tanggal *</Label>
                <Input
                  id="expense_date"
                  type="date"
                  value={formData.expense_date}
                  onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                  required
                  autoComplete="off"
                  readOnly={lockedByClosedPeriod}
                  className={lockedByClosedPeriod ? 'bg-slate-100 cursor-not-allowed' : ''}
                />
              </div>

              <div>
                <Label htmlFor="amount">Jumlah (Rp) *</Label>
                <Input
                  id="amount"
                  data-testid="expense-amount-input"
                  type="text"
                  value={formatNumber(formData.amount)}
                  onChange={handleAmountChange}
                  required
                  autoComplete="off"
                  readOnly={lockedByClosedPeriod}
                  className={lockedByClosedPeriod ? 'bg-slate-100 cursor-not-allowed' : ''}
                />
              </div>

              <div>
                <Label htmlFor="description">Deskripsi *</Label>
                <Input
                  id="description"
                  data-testid="expense-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  autoComplete="off"
                />
              </div>

              <div>
                <Label htmlFor="partai">Partai</Label>
                <Input
                  id="partai"
                  value={formData.partai}
                  onChange={(e) => setFormData({ ...formData, partai: e.target.value })}
                  autoComplete="off"
                />
              </div>
              <DialogFooter className="flex justify-between items-center">
                <div>
                  {editingId && (
                    <Button 
                      type="button" 
                      variant="destructive" 
                      onClick={() => handleDeleteClick(editingId)}
                      disabled={loading}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      <Trash2 size={16} className="mr-2" />
                      Hapus
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
                  <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={loading} data-testid="expense-submit-btn">
                    {loading ? 'Menyimpan...' : 'Simpan'}
                  </Button>
                </div>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
          
          {activePeriod && (
            <Dialog open={bulkDialogOpen} onOpenChange={(open) => { setBulkDialogOpen(open); if (!open) resetBulkForm(); }}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-teal-600 text-teal-600 hover:bg-teal-50" data-testid="bulk-add-expense-btn">
                  <Receipt size={20} className="mr-2" />
                  Input Bulk
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <div className="flex items-center justify-between">
                    <DialogTitle>Input Biaya Bulk</DialogTitle>
                    <div className="text-right">
                      <p className="text-sm text-slate-500 mb-1">Total Biaya</p>
                      <p className="text-2xl font-bold text-teal-600">
                        {formatCurrency(bulkFormData.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0))}
                      </p>
                    </div>
                  </div>
                </DialogHeader>
                <form onSubmit={handleBulkSubmit} className="space-y-4">
                  {/* Form Header */}
                  <div className="grid grid-cols-2 gap-4 p-4 border rounded-lg">
                    <div className="col-span-2">
                      <Label htmlFor="bulk_account">Rekening Cabang *</Label>
                      <SearchableSelect
                        options={branchAccounts.map(account => ({
                          value: account.id,
                          label: `${account.account_name} (Saldo: ${formatCurrency(account.balance)})`
                        }))}
                        value={bulkFormData.branch_account_id}
                        onValueChange={(value) => setBulkFormData({ ...bulkFormData, branch_account_id: value })}
                        placeholder="Pilih rekening..."
                        searchPlaceholder="Cari rekening..."
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <Label htmlFor="bulk_partai">Partai</Label>
                      <Input
                        id="bulk_partai"
                        value={bulkFormData.partai}
                        onChange={(e) => setBulkFormData({ ...bulkFormData, partai: e.target.value })}
                        placeholder="Opsional - Nama partai"
                      />
                    </div>
                  </div>

                  {/* Items Table */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Daftar Biaya</Label>
                      <Button type="button" size="sm" variant="outline" onClick={addBulkItem}>
                        <Plus size={16} className="mr-1" />
                        Tambah Baris
                      </Button>
                    </div>
                    
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[150px]">Tanggal</TableHead>
                            <TableHead className="w-[150px]">Jumlah</TableHead>
                            <TableHead>Keterangan</TableHead>
                            <TableHead className="w-[80px]">Aksi</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {bulkFormData.items.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Input
                                  ref={(el) => inputRefs.current[`${index}-expense_date`] = el}
                                  type="date"
                                  value={item.expense_date}
                                  onChange={(e) => updateBulkItem(index, 'expense_date', e.target.value)}
                                  onKeyDown={(e) => handleKeyDown(e, index, 'expense_date')}
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  ref={(el) => inputRefs.current[`${index}-amount`] = el}
                                  type="text"
                                  inputMode="numeric"
                                  value={formatBulkAmount(item.amount)}
                                  onChange={(e) => updateBulkItem(index, 'amount', e.target.value)}
                                  onKeyDown={(e) => handleKeyDown(e, index, 'amount')}
                                  placeholder="0"
                                  className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  ref={(el) => inputRefs.current[`${index}-description`] = el}
                                  value={item.description}
                                  onChange={(e) => updateBulkItem(index, 'description', e.target.value)}
                                  onKeyDown={(e) => handleKeyDown(e, index, 'description')}
                                  placeholder="Keterangan biaya"
                                />
                              </TableCell>
                              <TableCell>
                                {bulkFormData.items.length > 1 && (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => removeBulkItem(index)}
                                  >
                                    <Trash2 size={16} className="text-red-600" />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setBulkDialogOpen(false)}>
                      Batal
                    </Button>
                    <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={loading}>
                      {loading ? 'Menyimpan...' : `Simpan ${bulkFormData.items.length} Biaya`}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        
        {selectedExpenses.length > 0 && (
          <>
            <Button 
              variant="outline" 
              onClick={() => setBulkEditDialogOpen(true)}
              data-testid="bulk-edit-expense-btn"
            >
              Edit Partai ({selectedExpenses.length})
            </Button>
            <Button 
              variant="outline"
              onClick={() => setMoveDialogOpen(true)}
              className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300"
              data-testid="move-expense-to-period-btn"
            >
              <ArrowRightLeft size={16} className="mr-2" />
              Pindahkan ke Periode Lain ({selectedExpenses.length})
            </Button>
          </>
        )}
        </div>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Konfirmasi Hapus</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">Yakin ingin menghapus biaya ini? Tindakan ini tidak dapat dibatalkan.</p>
          </div>
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setDeleteDialogOpen(false);
                setDeletingId(null);
              }}
            >
              Batal
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700"
            >
              {loading ? 'Menghapus...' : 'Ya, Hapus'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move to Period Dialog */}
      <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <ArrowRightLeft size={20} />
              Pindahkan Biaya ke Periode Lain
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Memindahkan {selectedExpenses.length} biaya yang dipilih ke periode lain. 
              Biaya akan dihapus dari periode saat ini dan total rupiah akan dicatat di activity log untuk tracking.
            </p>
            <div>
              <Label htmlFor="move_target_period_expense">Pilih Periode Tujuan (Hanya Periode Aktif) *</Label>
              <Select value={selectedTargetPeriod} onValueChange={setSelectedTargetPeriod}>
                <SelectTrigger id="move_target_period_expense">
                  <SelectValue placeholder="Pilih periode..." />
                </SelectTrigger>
                <SelectContent>
                  {activePeriods.length === 0 ? (
                    <SelectItem value="none" disabled>Tidak ada periode aktif lain</SelectItem>
                  ) : (
                    activePeriods.map(period => (
                      <SelectItem key={period.id} value={period.id}>
                        {period.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => {
              setMoveDialogOpen(false);
              setSelectedTargetPeriod('');
            }}>
              Batal
            </Button>
            <Button 
              onClick={handleMoveToPeriod} 
              className="bg-blue-600 hover:bg-blue-700"
              disabled={loading || !selectedTargetPeriod}
            >
              {loading ? 'Memindahkan...' : 'Pindahkan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>

      {!activePeriod && (
        <Card className="border-l-4 border-l-yellow-500 bg-yellow-50">
          <CardContent className="pt-6">
            <p className="text-yellow-800">Tidak ada periode aktif. Buat periode baru di menu Periode untuk mulai mencatat transaksi.</p>
          </CardContent>
        </Card>
      )}

      {expenses.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Receipt size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600">Belum ada data biaya.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Daftar Biaya : {branchName}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table className="text-xs">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]">
                      <Checkbox
                        checked={selectedExpenses.length === expenses.length && expenses.length > 0}
                        onCheckedChange={toggleSelectAll}
                      />
                    </TableHead>
                    <TableHead className="w-[100px]">Tanggal</TableHead>
                    <TableHead className="w-[100px]">Partai</TableHead>
                    <TableHead className="w-[120px] text-right">Jumlah</TableHead>
                    <TableHead className="w-[300px]">Deskripsi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    // Group expenses by partai
                    const groupedByPartai = expenses.reduce((acc, expense) => {
                      const partai = expense.partai || 'Tanpa Partai';
                      if (!acc[partai]) acc[partai] = [];
                      acc[partai].push(expense);
                      return acc;
                    }, {});

                    return Object.entries(groupedByPartai).map(([partai, partaiExpenses]) => {
                      const partaiTotal = partaiExpenses.reduce((sum, exp) => sum + exp.amount, 0);
                      
                      return (
                        <React.Fragment key={partai}>
                          {/* Partai Header */}
                          <TableRow className="bg-teal-50 border-t-2 border-teal-300">
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={partaiExpenses.every(exp => selectedExpenses.includes(exp.id))}
                                onCheckedChange={() => togglePartaiExpenses(partaiExpenses.map(exp => exp.id))}
                                data-testid={`select-partai-expenses-${partai}`}
                              />
                            </TableCell>
                            <TableCell colSpan={4} className="font-bold text-teal-700">
                              <div className="grid grid-cols-3 items-center">
                                <span className="font-semibold text-teal-800 justify-self-start">PARTAI: {partai}</span>
                                <span className="justify-self-center text-blue-600 font-bold text-lg">{getBranchName(partaiExpenses[0]?.branch_id)}</span>
                                <span className="font-semibold text-green-600 justify-self-end">{formatCurrency(partaiTotal)}</span>
                              </div>
                            </TableCell>
                          </TableRow>
                          
                          {/* Expenses in this partai */}
                          {partaiExpenses.map((expense) => (
                            <TableRow 
                              key={expense.id} 
                              data-testid={`expense-row-${expense.id}`}
                              className="hover:bg-blue-50 transition-colors"
                            >
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                  checked={selectedExpenses.includes(expense.id)}
                                  onCheckedChange={() => toggleSelectExpense(expense.id)}
                                />
                              </TableCell>
                              <TableCell 
                                className="cursor-pointer"
                                onClick={() => handleEdit(expense)}
                              >
                                {formatDate(expense.expense_date)}
                              </TableCell>
                              <TableCell 
                                className="cursor-pointer"
                                onClick={() => handleEdit(expense)}
                              >
                                <span className="font-medium">{expense.partai || '-'}</span>
                              </TableCell>
                              <TableCell 
                                className="text-right font-semibold text-red-600 cursor-pointer"
                                onClick={() => handleEdit(expense)}
                              >
                                {formatCurrency(expense.amount)}
                              </TableCell>
                              <TableCell 
                                className="text-slate-600 cursor-pointer"
                                onClick={() => handleEdit(expense)}
                              >
                                {expense.description || '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      );
                    });
                  })()}
                </TableBody>
              </Table>
            </div>

            {/* Total Keseluruhan */}
            <div className="mt-4 pt-4 border-t flex justify-end">
              <div className="text-right">
                <p className="text-sm text-slate-600 mb-1">Total Biaya Keseluruhan</p>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(expenses.reduce((sum, expense) => sum + expense.amount, 0))}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Bulk Edit Partai Dialog */}
      <Dialog open={bulkEditDialogOpen} onOpenChange={setBulkEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Partai Massal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Mengubah partai untuk {selectedExpenses.length} biaya yang dipilih
            </p>
            <div>
              <Label htmlFor="bulk_partai">Partai Baru *</Label>
              <Input
                id="bulk_partai"
                value={bulkPartai}
                onChange={(e) => setBulkPartai(e.target.value)}
                placeholder="Masukkan nama partai"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setBulkEditDialogOpen(false);
                setBulkPartai('');
              }}
            >
              Batal
            </Button>
            <Button
              onClick={handleBulkEditPartai}
              disabled={loading || !bulkPartai.trim()}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {loading ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Expenses;
